import { FlavorApi } from 'infra/api/FlavorApi';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct/src/index';
import { createSelector } from 'reselect';
import {
    AvailabilityZonesUtils,
    DISABLED_AVAILABILITY_ZONE_NAMES,
} from 'services/infra/helpers/availabilityZonesUtils';
import { stateHelper } from 'utilities/stateHelper';
import { reduxHelper } from 'utils/reduxHelper';

import { AvailabilityZoneSelect } from 'reactApp/components/Forms/AvailabilityZoneSelect';

const flavorsModule = stateHelper.createModuleState('flavors', {
    list: [],
    isLoaded: false,
    isLoading: false,
    hasError: false,
});

export const flavorsActions = {
    loadFlavors: () => async (dispatch) => {
        dispatch(flavorsModule.setPart('isLoading', true));

        try {
            const list = await FlavorApi.loadList();

            dispatch(
                flavorsModule.mergeModuleState({
                    list,
                    isLoaded: true,
                    isLoading: false,
                }),
            );

            return list;
        } catch (error) {
            dispatch(
                flavorsModule.mergeModuleState({
                    hasError: true,
                    isLoaded: false,
                    isLoading: false,
                }),
            );

            console.error('flavor list loading error', error);
            throw error;
        }
    },
};

const selectList = createSelector(
    (state) => flavorsModule.getPart(state, 'list'),
    (list) => list.sort(_sortFlavors).filter(_filterFlavorsByAvailableZones),
);

const _getShortFlavorName = (name) =>
    String(name).replace(/(-\d+-\d+)-\d+$/, '$1');

const selectListExtended = (state) => {
    const list = selectList(state);

    return list.map((flavor) => {
        return {
            ...flavor,
            customFields: {
                shortName: _getShortFlavorName(flavor.name) || flavor.name,
            },
        };
    });
};

const selectListUnique = (state) => {
    const list = selectListExtended(state);
    const flavors = new Map();

    for (const currentFlavor of list) {
        const { shortName } = currentFlavor.customFields;
        const availabilityZone = _getAvailabilityZone(currentFlavor);
        const generatedKey = availabilityZone
            ? `${shortName} (${availabilityZone})`
            : shortName;
        const existedFlavor = flavors.get(generatedKey);

        if (!existedFlavor || existedFlavor.disk > currentFlavor.disk) {
            flavors.set(generatedKey, currentFlavor);
        }
    }

    return Array.from(flavors, ([, value]) => value);
};

const selectListSupplementedFlavorId = (state, missingFlavorId) => {
    let flavorsUnique = selectListUnique(state);

    if (!flavorsUnique.some((item) => item.id === missingFlavorId)) {
        const missingFlavor = selectFlavor(state, missingFlavorId);
        if (missingFlavor) {
            flavorsUnique = flavorsUnique.filter(
                (item) =>
                    item.customFields.shortName !==
                    missingFlavor.customFields.shortName,
            );

            flavorsUnique.unshift(missingFlavor);
        }
    }

    return flavorsUnique;
};

const _sortFlavors = (flavorA, flavorB) => {
    if (flavorA.vcpus !== flavorB.vcpus) {
        return flavorA.vcpus < flavorB.vcpus ? -1 : 1;
    }

    if (flavorA.ram !== flavorB.ram) {
        return flavorA.ram < flavorB.ram ? -1 : 1;
    }

    if (flavorA.disk !== flavorB.disk) {
        return flavorA.disk < flavorB.disk ? -1 : 1;
    }

    return 0;
};

const _filterFlavorsByAvailableZones = (flavor) => {
    let availabilityZones = _getAvailabilityZone(flavor);
    let isFlavorAvailable = true;

    if (RA.isString(availabilityZones)) {
        availabilityZones = availabilityZones.split(',');

        availabilityZones.forEach((zone) => {
            if (DISABLED_AVAILABILITY_ZONE_NAMES.includes(zone)) {
                isFlavorAvailable = false;
            }
        });
    }

    return isFlavorAvailable;
};

const _findFlavorById = (flavors, targetId) =>
    flavors.find(
        // TODO: сверка по name это костыль, убрать после выяснения обстоятельств
        ({ name, id }) => id === targetId || name === targetId,
    );

const _findFlavorByName = (flavors, targetName) =>
    flavors.find(({ name }) => name === targetName);

const selectFlavor = reduxHelper.createSelectorWithArguments(
    selectListExtended,
    (list, id) => _findFlavorById(list, id),
);

const selectFlavorByName = reduxHelper.createSelectorWithArguments(
    selectListExtended,
    (list, name) => _findFlavorByName(list, name),
);

const selectContainersFlavorsList = createSelector(selectListUnique, (list) =>
    list.filter(_doesFlavorFitForContainers),
);

const selectContainersHighCpuFlavors = createSelector(
    selectContainersFlavorsList,
    (list) => list.filter(isFlavorHighCpu),
);
const selectIsLoading = (state) => flavorsModule.getPart(state, 'isLoading');
const selectIsLoaded = (state) => flavorsModule.getPart(state, 'isLoaded');
const selectHasError = (state) => flavorsModule.getPart(state, 'hasError');

const selectContainerFlavors = reduxHelper.createSelectorWithArguments(
    selectListExtended,
    (flavors, { master_flavor_id, flavor_id }) => {
        return {
            masterFlavor: _findFlavorById(flavors, master_flavor_id),
            nodeFlavorDeprecated: _findFlavorById(flavors, flavor_id), // deprecated because it used only for old clusters without nodegroups
        };
    },
);

const selectNodegroupFlavor = reduxHelper.createSelectorWithArguments(
    selectListExtended,
    (flavors, { flavor_id }) => {
        return _findFlavorById(flavors, flavor_id);
    },
);

const getHighCpuFlavors = reduxHelper.createSelectorWithArguments(
    selectListExtended,
    (flavors) => {
        return flavors.filter(isFlavorHighCpu);
    },
);

const _doesFlavorFitForContainers = (flavor) => flavor.ram >= 2048;
const getStandardCpuFlavors = (flavors) =>
    flavors.filter((flavor) => isFlavorStandardCpu(flavor));
const isFlavorStandardCpu = (flavor) => _getCpuType(flavor) === 'standard';
const isFlavorHighCpu = (flavor) => _getCpuType(flavor) === 'hpc';
const _getCpuType = (flavor) => R.path(['extra_specs', 'mcs:cpu_type'], flavor);

const _getIsGpu = (flavor) => R.path(['extra_specs', 'agg_gpu'], flavor);
const getGpuFlavors = reduxHelper.createSelectorWithArguments(
    selectListUnique,
    (flavors) => flavors.filter(_getIsGpu),
);

const generateFlavorId = (
    highCpuFlavors,
    minFlavor,
    flavors,
    cpuCheckboxFieldValue,
) => {
    const standardCpuFlavors = flavorsUtils.getStandardCpuFlavors(flavors);

    let filteredFlavors = cpuCheckboxFieldValue
        ? highCpuFlavors
        : standardCpuFlavors;

    if (minFlavor && !cpuCheckboxFieldValue) {
        // handle minFlavor
        // ignore minFlavor for highCpuFlavors
        const isValueSatisfies = (key, flavor) => {
            return R.gte(flavor[key] || 0, minFlavor[key] || 0);
        };

        filteredFlavors = filteredFlavors.filter((flavor) => {
            return ['vcpus', 'ram', 'disk'].every((key) =>
                isValueSatisfies(key, flavor),
            );
        });
    }

    return filteredFlavors[0].id;
};

const generateAvailabilityZoneName = (
    availabilityZones,
    availabilityZoneFieldValue,
) => {
    return availabilityZoneFieldValue === 'auto'
        ? AvailabilityZoneSelect.getRandomAvailabilityZone({
              availabilityZones,
          })
        : availabilityZoneFieldValue;
};

const getAvailabilityZonesForFlavor = (flavors, flavorId) => {
    const flavor = flavors.find((item) => item.id === flavorId);

    let availabilityZones = _getAvailabilityZone(flavor);
    if (RA.isString(availabilityZones)) {
        availabilityZones = availabilityZones.split(',');
    }

    return availabilityZones;
};

const generateAvailabilityZone = (
    flavors,
    flavorFieldValue,
    availabilityZoneFieldValue,
) => {
    const availabilityZonesForFlavor = flavorsUtils.getAvailabilityZonesForFlavor(
        flavors,
        flavorFieldValue,
    );

    return availabilityZonesForFlavor && availabilityZonesForFlavor.length === 1
        ? availabilityZonesForFlavor[0].toUpperCase()
        : availabilityZoneFieldValue;
};
const _getAvailabilityZone = (flavor) => {
    return R.path(['extra_specs', 'mcs:availability_zone'], flavor);
};

const getAllAvailabilityZones = (availabilityZones) => {
    const options = [
        {
            text: 'Автоматически',
            value: 'auto',
            key: 'auto',
        },
    ];

    const getZoneNameAsText = (zone) => {
        return AvailabilityZonesUtils.getFullName(zone);
    };

    return availabilityZones.reduce((options, { zoneName }) => {
        options.push({
            text: getZoneNameAsText(zoneName),
            value: zoneName,
            key: zoneName,
        });
        return options;
    }, options);
};

const getListForUserChoice = (state, { service } = {}) =>
    service === 'gpu' ? getGpuFlavors(state) : selectListUnique(state);

export const flavorsSelectors = {
    selectList,
    selectFlavor,
    selectFlavorByName,
    selectContainerFlavors,
    selectContainersFlavorsList,
    selectContainersHighCpuFlavors,
    selectNodegroupFlavor,
    selectIsLoading,
    selectIsLoaded,
    selectHasError,
    getHighCpuFlavors,
    getGpuFlavors,
    selectListExtended,
    selectListUnique,
    selectListSupplementedFlavorId,
    getListForUserChoice,
};

const getFlavorByNamePart = ({ namePart, flavors }) => {
    let flavor = flavors.find(({ name }) => name.includes(namePart));
    if (!flavor) {
        console.error("can't find flavor with name includes " + namePart);
        flavor = flavors[0];
    }

    return flavor;
};
export const flavorsUtils = {
    getFlavorByNamePart,
    isFlavorHighCpu,
    isFlavorStandardCpu,
    getStandardCpuFlavors,
    generateFlavorId,
    generateAvailabilityZoneName,
    getAvailabilityZonesForFlavor,
    generateAvailabilityZone,
    getAllAvailabilityZones,
};

export const BoundFlavorsSelectors = reduxHelper.bindSelectors(
    flavorsSelectors,
);

export const BoundFlavorsActions = reduxHelper.bindActions(flavorsActions);
